import { gql, useMutation, useApolloClient } from '@apollo/client';
import {
  Task,
  updateTask as UPDATE_TASK,
  getTask as GET_TASK,
  updateBindingTask as UPDATE_BINDING_TASK,
  BindingStatus,
  TaskStatus,
} from '@admiin-com/ds-graphql';
import { UseMutationTuple } from '../useCreateContact/useCreateContact';
import useTasksFromurlByFirmId from '../../features/hooks/useTasksFromUrl/useTasksFromUrlByFirmId';
import { convertToBackendTask } from '../../helpers/tasks';

export const useUpdateTask = (
  task?: Task | null
): [
  (options: any) => Promise<any>,
  { error: UseMutationTuple[1]['error']; loading: boolean }
] => {
  const { refetch } = useTasksFromurlByFirmId({ disableAutoFetch: true });
  const client = useApolloClient();
  const [updateTaskMutation, data] = useMutation(gql(UPDATE_TASK), {
    update(cache, { data: { updateTask } }) {
      // Write the updated Task into the cache
      cache.writeFragment({
        id: cache.identify({ __typename: 'Task', id: updateTask.id }),
        fragment: gql`
          fragment UpdatedTask on Task {
            id
            status
            direction
            type
            category
            invoiceStatus
            dueAt
            amount
            fromId
            paymentStatus
            signatureStatus
            toId
          }
        `,
        data: updateTask,
      });

      // Instead of querying searchTasks, filter out the task with outdated status
      // from any existing query results
      cache.modify({
        fields: {
          searchTasks(existing, { readField, toReference }) {
            if (!existing || !existing.items) {
              return existing;
            }

            const updatedTaskId = updateTask.id;

            // Always remove the task with the given ID from the results
            // This ensures the outdated version from OpenSearch isn't shown
            // regardless of which fields were updated
            const filteredItems = existing.items.filter((itemRef: any) => {
              const id = readField('id', itemRef);
              return id !== updatedTaskId;
            });

            // Add the updated task using the reference to the cache entry we just updated
            const updatedTaskRef = toReference({
              __typename: 'Task',
              id: updatedTaskId,
            });
            if (updatedTaskRef) {
              // Insert the updated task at the beginning to make it more visible
              filteredItems.unshift(updatedTaskRef);
            }

            return {
              ...existing,
              items: filteredItems,
            };
          },
          // Also update any task-specific queries that might be in the cache
          getTask(existing, { toReference }) {
            // If this query is for the task we just updated, return the updated task
            if (existing && existing.id === updateTask.id) {
              const updatedTaskRef = toReference({
                __typename: 'Task',
                id: updateTask.id,
              });
              return updatedTaskRef || existing;
            }
            return existing;
          },
        },
      });

      // Remove the refetch call here as OpenSearch results are delayed
      // And we're already updating the Apollo cache manually above
      // refetch();
    },
  });

  // Define a query to fetch a task by ID when needed
  const fetchTaskById = async ({
    id,
    entityId,
  }: {
    id: string;
    entityId: string;
  }) => {
    try {
      const result = await client.query({
        query: gql(GET_TASK),
        variables: { id, entityId },
        fetchPolicy: 'network-only', // Force network request to get fresh data
      });
      return result.data.getTask;
    } catch (error) {
      console.error('Error fetching task after failed update:', error);
      return null;
    }
  };

  const [updateBindingTask] = useMutation(gql(UPDATE_BINDING_TASK));

  const updateTask = async (options: any) => {
    // Convert the task variables to backend format before sending
    if (options.variables?.input) {
      options.variables.input = convertToBackendTask(options.variables.input);
    }

    try {
      const result = await updateTaskMutation(options);
      const updatedStatus: TaskStatus | undefined =
        result?.data?.updateTask?.status;
      if (
        task?.bindingId &&
        (task.status === TaskStatus.DRAFT ||
          task.status === TaskStatus.REVIEW) &&
        updatedStatus &&
        updatedStatus !== TaskStatus.DRAFT &&
        updatedStatus !== TaskStatus.REVIEW
      ) {
        await updateBindingTask({
          variables: {
            input: { id: task.bindingId, status: BindingStatus.SENT },
          },
        });
      }
      return result;
    } catch (error) {
      console.error('Task update failed:', error);

      // If the update fails and we have a task ID, try to get the latest task data
      if (options.variables?.input?.id) {
        console.log('Fetching latest task data after failed update');
        const latestTask = await fetchTaskById({
          id: options.variables.input.id,
          entityId: options.variables.input.entityId,
        });

        // We can return an object with the error and the latest task data
        return {
          error,
          latestTask,
        };
      }

      // Re-throw the error if we can't fetch the latest task
      throw error;
    }
  };

  return [updateTask, { loading: data.loading, error: data.error }];
};
